// Electric ice kart, built from reference photos (WS-13, round 4b). Generic vehicle asset for tracks.js.
// Reference: the resort's karts (refs/user/feedback-round4b/07-09, refs/photos/winter-ready/Karting - *):
// a low, faceted graphite body (full-width nose with a centre V, raised front corner fenders, angular side pods,
// tall steering shield with a number plate, wide battery cover behind the seat), a black tubular bumper cage all
// round (front bar with a shallow V and corner hoops, side nerf bars, rear bar with a V), white LED bars in the
// nose and on the front fenders, red rear lights, studded ice tyres on silver hubs, a moulded seat, a driver in
// dark overalls and a helmet. Proportions of a ~1.95 x 1.36 m rental e-kart. No logos or branding.
// Frame: +x forward, +y up, +z = the kart's right; the floor sits on y = 0 minus the tyre radius (wheels touch y = 0).
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const C = { body: 0x2c3036, bodyHi: 0x454b54, chassis: 0x17181b, cage: 0x111214, seat: 0x141517, suit: 0x1c1f25,
  glove: 0x0f1012, helmet: 0x101113, visor: 0x2a3440, boot: 0x1a1a1c, column: 0x8d9299 };

function paint(g, hex) {
  g = g.index ? g.toNonIndexed() : g;
  const c = new THREE.Color(hex), n = g.attributes.position.count, a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) c.toArray(a, i * 3);
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  g.deleteAttribute('uv');
  return g;
}
const merge = (list) => mergeGeometries(list.map((g) => { g = g.index ? g.toNonIndexed() : g; if (g.attributes.uv && !list.every((h) => h.attributes.uv)) g.deleteAttribute('uv'); return g; }));

/** Faceted solid lofted through cross-sections [{ x, pts: [[z, y], ...] }] (same point count, CCW seen from +x). */
function loft(sections, hex, mirrorZ = false) {
  const pos = [];
  const P = (x, z, y) => [x, y, mirrorZ ? -z : z];
  const tri = (a, b, c) => (mirrorZ ? pos.push(...a, ...c, ...b) : pos.push(...a, ...b, ...c));
  for (let s = 0; s < sections.length - 1; s++) {
    const A = sections[s], B = sections[s + 1], n = A.pts.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const a0 = P(A.x, ...A.pts[i]), a1 = P(A.x, ...A.pts[j]), b0 = P(B.x, ...B.pts[i]), b1 = P(B.x, ...B.pts[j]);
      tri(a0, b0, b1); tri(a0, b1, a1);
    }
  }
  for (const [S, flip] of [[sections[0], true], [sections[sections.length - 1], false]]) { // caps (fan)
    const c = S.pts.reduce((m, p) => [m[0] + p[0] / S.pts.length, m[1] + p[1] / S.pts.length], [0, 0]);
    for (let i = 0; i < S.pts.length; i++) {
      const a = P(S.x, ...c), b = P(S.x, ...S.pts[i]), d = P(S.x, ...S.pts[(i + 1) % S.pts.length]);
      flip ? tri(a, d, b) : tri(a, b, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return paint(g, hex);
}
const box = (sx, sy, sz, x, y, z, hex, ry = 0, rz = 0) => { const g = new THREE.BoxGeometry(sx, sy, sz); if (rz) g.rotateZ(rz); if (ry) g.rotateY(ry); return paint(g.translate(x, y, z), hex); };
function tube(pts, r, hex, seg = 6) {
  const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)), false, 'catmullrom', 0.08);
  return paint(new THREE.TubeGeometry(curve, pts.length * 8, r, seg, false), hex);
}
function capsule(a, b, r, hex, seg = 8) {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), len = A.distanceTo(B);
  const g = new THREE.CapsuleGeometry(r, len, 3, seg);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), B.clone().sub(A).normalize()));
  g.translate((A.x + B.x) / 2, (A.y + B.y) / 2, (A.z + B.z) / 2);
  return paint(g, hex);
}

/** Wheel positions (kart frame) and sizes; r = tyre radius, w = tyre width. */
export const KART_WHEELS = [
  { x: 0.56, z: -0.56, r: 0.125, w: 0.13, front: true }, { x: 0.56, z: 0.56, r: 0.125, w: 0.13, front: true },
  { x: -0.62, z: -0.62, r: 0.14, w: 0.18, front: false }, { x: -0.62, z: 0.62, r: 0.14, w: 0.18, front: false },
];
export const KART_WHEELBASE = 1.18;

/** Unit wheel (radius 1, width 1, axle along z), studded tread and a silver hub: scale per instance. */
export function wheelGeometry(detail = 'hi') {
  const seg = detail === 'hi' ? 20 : 10, parts = [paint(new THREE.CylinderGeometry(1, 1, 1, seg, 1).rotateX(Math.PI / 2), 0x151517)];
  parts.push(paint(new THREE.CylinderGeometry(0.5, 0.5, 1.02, seg, 1).rotateX(Math.PI / 2), 0x9ba1a8)); // silver hub faces
  if (detail === 'hi') {
    parts.push(paint(new THREE.CylinderGeometry(0.2, 0.2, 1.08, 8, 1).rotateX(Math.PI / 2), 0x3a3d42)); // axle nut
    for (let i = 0; i < 16; i++) { // tread blocks with studs: they make the rolling visible
      const t = (i / 16) * Math.PI * 2;
      parts.push(paint(new THREE.BoxGeometry(0.16, 0.07, 0.84).rotateZ(t).translate(Math.cos(t) * 1.0, Math.sin(t) * 1.0, 0), 0x202124));
    }
  }
  return merge(parts);
}

/** Kart body (with driver and bumper cage), vertex-coloured. detail 'hi' | 'lo'. Separate parts for the
 *  number plate (uv 0..1), front LEDs and rear lights, so they can take their own materials. */
export function kartGeometry(detail = 'hi') {
  const L = [];
  if (detail === 'lo') {
    L.push(box(1.7, 0.08, 1.0, 0, 0.1, 0, C.chassis));
    L.push(box(0.45, 0.2, 1.15, 0.72, 0.2, 0, C.body), box(0.9, 0.26, 0.22, -0.05, 0.24, 0.5, C.body), box(0.9, 0.26, 0.22, -0.05, 0.24, -0.5, C.body));
    L.push(box(0.4, 0.32, 1.0, -0.74, 0.26, 0, C.body), box(0.4, 0.4, 0.3, 0.32, 0.45, 0, C.body, 0, 0.6));
    L.push(box(0.36, 0.5, 0.42, -0.34, 0.55, 0, C.suit), paint(new THREE.SphereGeometry(0.15, 8, 6).translate(-0.26, 0.92, 0), C.helmet));
    for (const s of [-1, 1]) L.push(box(1.9, 0.05, 0.05, 0, 0.12, s * 0.69, C.cage), box(0.05, 0.3, 1.3, s * 1.0, 0.22, 0, C.cage));
    return { body: merge(L), plate: null, ledF: merge([box(0.03, 0.03, 0.3, 0.95, 0.17, 0, 0xffffff)]), ledR: merge([box(0.05, 0.03, 0.06, -0.9, 0.42, -0.36, 0xffffff), box(0.05, 0.03, 0.06, -0.9, 0.42, 0.36, 0xffffff)]) };
  }
  // chassis tray
  L.push(box(1.72, 0.05, 1.02, 0, 0.085, 0, C.chassis));
  // full-width nose with a centre V and a lip
  L.push(loft([
    { x: 0.50, pts: [[0.6, 0.09], [0.6, 0.25], [0.2, 0.31], [0, 0.315], [-0.2, 0.31], [-0.6, 0.25], [-0.6, 0.09]].map(([z, y]) => [-z, y]) },
    { x: 0.86, pts: [[0.57, 0.09], [0.57, 0.19], [0.16, 0.23], [0, 0.205], [-0.16, 0.23], [-0.57, 0.19], [-0.57, 0.09]].map(([z, y]) => [-z, y]) },
    { x: 0.96, pts: [[0.5, 0.1], [0.5, 0.145], [0.13, 0.16], [0, 0.135], [-0.13, 0.16], [-0.5, 0.145], [-0.5, 0.1]].map(([z, y]) => [-z, y]) },
  ], C.body));
  // raised front corner fenders over the front wheels
  for (const m of [false, true]) {
    L.push(loft([
      { x: 0.36, pts: [[0.46, 0.2], [0.46, 0.34], [0.53, 0.39], [0.62, 0.36], [0.62, 0.2]] },
      { x: 0.70, pts: [[0.45, 0.2], [0.45, 0.36], [0.53, 0.41], [0.62, 0.38], [0.62, 0.2]] },
      { x: 0.86, pts: [[0.47, 0.2], [0.47, 0.31], [0.53, 0.34], [0.6, 0.32], [0.6, 0.2]] },
    ], C.body, m));
    // side pod, rising towards the rear
    L.push(loft([
      { x: 0.40, pts: [[0.38, 0.1], [0.38, 0.22], [0.5, 0.25], [0.6, 0.22], [0.6, 0.1]] },
      { x: -0.05, pts: [[0.37, 0.1], [0.37, 0.29], [0.5, 0.33], [0.61, 0.29], [0.61, 0.1]] },
      { x: -0.42, pts: [[0.37, 0.1], [0.37, 0.33], [0.5, 0.37], [0.6, 0.33], [0.6, 0.1]] },
    ], C.body, m));
    L.push(box(0.5, 0.012, 0.012, 0.0, 0.31, m ? -0.61 : 0.61, C.bodyHi, 0, -0.08)); // pod crease highlight
  }
  // tall steering shield (number plate on its sloped top)
  L.push(loft([
    { x: 0.64, pts: [[-0.1, 0.24], [-0.1, 0.3], [0, 0.315], [0.1, 0.3], [0.1, 0.24]] },
    { x: 0.36, pts: [[-0.15, 0.22], [-0.15, 0.47], [0, 0.5], [0.15, 0.47], [0.15, 0.22]] },
    { x: 0.10, pts: [[-0.17, 0.22], [-0.17, 0.63], [0, 0.66], [0.17, 0.63], [0.17, 0.22]] },
    { x: 0.02, pts: [[-0.12, 0.22], [-0.12, 0.6], [0, 0.62], [0.12, 0.6], [0.12, 0.22]] },
  ], C.body));
  // battery cover behind the seat, between the rear wheels
  L.push(loft([
    { x: -0.50, pts: [[-0.5, 0.1], [-0.5, 0.36], [-0.2, 0.43], [0.2, 0.43], [0.5, 0.36], [0.5, 0.1]] },
    { x: -0.84, pts: [[-0.49, 0.1], [-0.49, 0.39], [-0.2, 0.45], [0.2, 0.45], [0.49, 0.39], [0.49, 0.1]] },
    { x: -0.93, pts: [[-0.44, 0.12], [-0.44, 0.32], [-0.18, 0.36], [0.18, 0.36], [0.44, 0.32], [0.44, 0.12]] },
  ], C.body));
  L.push(box(0.02, 0.05, 0.6, -0.935, 0.24, 0, C.bodyHi)); // rear panel recess
  // seat
  L.push(box(0.36, 0.08, 0.42, -0.3, 0.17, 0, C.seat), box(0.07, 0.5, 0.44, -0.52, 0.42, 0, C.seat, 0, 0.28));
  for (const s of [-1, 1]) L.push(box(0.3, 0.16, 0.05, -0.36, 0.25, s * 0.22, C.seat));
  // steering column and wheel
  L.push(capsule([0.12, 0.42, 0], [0.0, 0.66, 0], 0.02, C.column, 6));
  L.push(paint(new THREE.TorusGeometry(0.14, 0.018, 6, 18).rotateY(Math.PI / 2).rotateZ(0.5).translate(-0.02, 0.67, 0), C.cage));
  // driver: puffy overalls, arms to the wheel, helmet with visor; legs run under the shield
  L.push(capsule([-0.4, 0.3, 0], [-0.32, 0.6, 0], 0.18, C.suit, 10)); // torso, leaning back into the seat
  L.push(capsule([-0.31, 0.62, -0.17], [-0.31, 0.62, 0.17], 0.1, C.suit, 8)); // shoulders (puffy overalls)
  for (const s of [-1, 1]) {
    L.push(capsule([-0.3, 0.62, s * 0.22], [-0.13, 0.57, s * 0.2], 0.065, C.suit), capsule([-0.13, 0.57, s * 0.2], [-0.03, 0.65, s * 0.13], 0.055, C.suit));
    L.push(paint(new THREE.SphereGeometry(0.055, 8, 6).translate(-0.02, 0.67, s * 0.12), C.glove));
    L.push(capsule([-0.24, 0.24, s * 0.11], [0.22, 0.33, s * 0.12], 0.08, C.suit), capsule([0.22, 0.33, s * 0.12], [0.5, 0.27, s * 0.12], 0.065, C.suit));
  }
  L.push(capsule([-0.32, 0.72, 0], [-0.3, 0.78, 0], 0.075, C.suit, 8)); // collar
  L.push(paint(new THREE.SphereGeometry(0.145, 14, 10).scale(1.12, 1, 0.98).translate(-0.29, 0.9, 0), C.helmet)); // helmet shell
  L.push(paint(new THREE.SphereGeometry(0.147, 12, 6, Math.PI - 0.85, 1.7, 1.2, 0.55).scale(1.12, 1, 0.98).translate(-0.285, 0.9, 0), C.visor)); // visor
  L.push(capsule([-0.2, 0.8, -0.07], [-0.2, 0.8, 0.07], 0.05, C.helmet, 8)); // chin bar
  // bumper cage (black steel tubes)
  const r = 0.02, cage = C.cage;
  L.push(tube([[0.8, 0.1, -0.68], [0.97, 0.1, -0.63], [1.02, 0.1, -0.3], [1.02, 0.1, 0.3], [0.97, 0.1, 0.63], [0.8, 0.1, 0.68]], r, cage));
  L.push(tube([[0.86, 0.12, -0.67], [0.93, 0.29, -0.63], [0.99, 0.29, -0.3], [1.01, 0.21, 0], [0.99, 0.29, 0.3], [0.93, 0.29, 0.63], [0.86, 0.12, 0.67]], r, cage));
  for (const s of [-1, 1]) {
    L.push(tube([[0.9, 0.12, s * 0.67], [0.93, 0.42, s * 0.65], [0.8, 0.44, s * 0.64], [0.74, 0.16, s * 0.64]], r, cage)); // front corner hoop
    L.push(tube([[0.8, 0.1, s * 0.68], [0.2, 0.1, s * 0.7], [-0.4, 0.1, s * 0.7], [-0.8, 0.1, s * 0.7]], r, cage)); // lower nerf bar
    L.push(tube([[0.55, 0.11, s * 0.7], [0.5, 0.25, s * 0.71], [-0.42, 0.25, s * 0.72], [-0.48, 0.11, s * 0.7]], r, cage)); // upper nerf bar
    L.push(tube([[-0.82, 0.1, s * 0.7], [-0.97, 0.1, s * 0.64], [-1.03, 0.1, s * 0.3], [-1.03, 0.1, 0]], r, cage)); // rear lower
    L.push(tube([[-1.0, 0.11, s * 0.31], [-1.01, 0.38, s * 0.3]], r, cage), tube([[-0.93, 0.11, s * 0.64], [-0.95, 0.39, s * 0.63]], r, cage)); // rear uprights
  }
  L.push(tube([[-0.82, 0.14, -0.7], [-0.95, 0.4, -0.65], [-1.01, 0.4, -0.3], [-1.03, 0.31, 0], [-1.01, 0.4, 0.3], [-0.95, 0.4, 0.65], [-0.82, 0.14, 0.7]], r, cage)); // rear top bar with V
  // number plate (sloped top of the shield): uv 0..1 for the digit atlas
  const yTop = (x) => (x > 0.36 ? 0.315 + ((0.64 - x) / 0.28) * 0.185 : 0.5 + ((0.36 - x) / 0.26) * 0.16);
  const plate = new THREE.BufferGeometry(), pa = [0.42, 0.095], pb = [0.2, 0.115];
  const v = [[pa[0], yTop(pa[0]) + 0.006, -pa[1]], [pa[0], yTop(pa[0]) + 0.006, pa[1]], [pb[0], yTop(pb[0]) + 0.006, pb[1]], [pb[0], yTop(pb[0]) + 0.006, -pb[1]]];
  plate.setAttribute('position', new THREE.Float32BufferAttribute([...v[0], ...v[1], ...v[2], ...v[0], ...v[2], ...v[3]], 3));
  plate.setAttribute('uv', new THREE.Float32BufferAttribute([1, 0, 0, 0, 0, 1, 1, 0, 0, 1, 1, 1], 2)); // u runs to the reader's right (-z), v up the slope
  plate.computeVertexNormals();
  const ledF = merge([box(0.025, 0.028, 0.3, 0.95, 0.165, 0, 0xffffff, 0, -0.5), box(0.02, 0.022, 0.1, 0.855, 0.315, -0.535, 0xffffff), box(0.02, 0.022, 0.1, 0.855, 0.315, 0.535, 0xffffff)]);
  const ledR = merge([box(0.05, 0.025, 0.07, -0.86, 0.445, -0.36, 0xffffff), box(0.05, 0.025, 0.07, -0.86, 0.445, 0.36, 0xffffff)]);
  return { body: merge(L), plate, ledF, ledR };
}

/** Digit atlas for the number plates: 8 white plates with black numbers 1..8 side by side (u = digit / 8). */
export function numberAtlas() {
  const c = document.createElement('canvas'); c.width = 512; c.height = 64;
  const g = c.getContext('2d');
  for (let i = 0; i < 8; i++) {
    g.fillStyle = '#e9ebee'; g.fillRect(i * 64 + 2, 2, 60, 60);
    g.fillStyle = '#16181b'; g.font = 'bold 50px Arial, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(String(i + 1), i * 64 + 32, 34);
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}
